
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Filter, ArrowUpDown, Clock, FileDown, Download } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { calculateReadingTime } from '@/lib/blog-utils';
import { exportBlogPostToPDF, exportAllBlogPostsToPDF } from '@/utils/pdfExport';

const BlogsPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [isExporting, setIsExporting] = useState(false);

  // Fetch blog posts from Supabase
  const { data: blogPosts = [], isLoading, error } = useQuery({
    queryKey: ['blogPosts'],
    queryFn: async () => {
      try {
        // First, fetch the blog posts
        const { data: posts, error: postsError } = await supabase
          .from('blog_posts')
          .select('*');
        
        if (postsError) {
          console.error('Error fetching blog posts:', postsError);
          throw postsError;
        }
        
        if (!posts || posts.length === 0) {
          return [];
        }

        // Then fetch author info separately for each post
        const postsWithAuthors = await Promise.all(
          posts.map(async (post) => {
            let authorData = { first_name: '', last_name: '' };
            
            if (post.author_id) {
              const { data: author, error: authorError } = await supabase
                .from('profiles')
                .select('first_name, last_name')
                .eq('id', post.author_id)
                .maybeSingle();
              
              if (!authorError && author) {
                authorData = author;
              }
            }
            
            // Fetch tags for the post
            const { data: tagData, error: tagError } = await supabase
              .from('blog_posts_tags')
              .select('tags (id, name)')
              .eq('blog_post_id', post.id);
            
            const tags = tagError || !tagData ? [] : tagData.map(t => t.tags);
            
            return {
              ...post,
              author: authorData,
              tags
            };
          })
        );

        return postsWithAuthors;
      } catch (error) {
        console.error('Error in fetching blog posts:', error);
        toast.error('Failed to load blog posts');
        return [];
      }
    }
  });

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  // Filter and sort the blog posts
  const filteredPosts = blogPosts
    .filter((post) => {
      // Filter by status
      if (filter !== 'all' && post.status !== filter) return false;
      
      // Filter by search term
      if (
        searchTerm &&
        !post.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !(post.author?.first_name + ' ' + post.author?.last_name).toLowerCase().includes(searchTerm.toLowerCase()) &&
        !post.tags?.some((tag) => tag.name.toLowerCase().includes(searchTerm.toLowerCase()))
      ) {
        return false;
      }
      
      return true;
    })
    .sort((a, b) => {
      // Sort by selected criteria
      if (sortBy === 'newest') {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      } else if (sortBy === 'oldest') {
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      } else if (sortBy === 'title') {
        return a.title.localeCompare(b.title);
      }
      return 0;
    });

  const handleExportSingle = async (post: any, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      toast.info(`Exporting "${post.title}"...`);
      await exportBlogPostToPDF(post);
      toast.success('PDF exported!');
    } catch {
      toast.error('Failed to export PDF');
    }
  };

  const handleExportBatch = async (posts: any[]) => {
    if (posts.length === 0) {
      toast.error('No posts to export');
      return;
    }
    setIsExporting(true);
    try {
      await exportAllBlogPostsToPDF(posts, (current, total) => {
        toast.info(`Exporting ${current}/${total}...`);
      });
      toast.success(`Exported ${posts.length} posts!`);
    } catch {
      toast.error('Failed to export PDFs');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Blog Posts</h1>
          <p className="text-muted-foreground">Manage your travel blog content</p>
        </div>
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={isExporting}>
                <Download className="h-4 w-4 mr-2" />
                Export PDF
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Export Options</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleExportBatch(blogPosts.filter(p => p.status === 'published'))}>
                All Published Posts
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExportBatch(filteredPosts)}>
                Filtered Posts ({filteredPosts.length})
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={() => navigate('/dashboard/blogs/new')}>
            <Plus className="h-4 w-4 mr-2" />
            New Post
          </Button>
        </div>
      </div>

      <Card className="glass-card">
        <CardHeader>
          <CardTitle>All Posts</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input 
                type="search" 
                placeholder="Search posts..." 
                className="pl-8"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-full sm:w-auto">
                  <Filter className="h-4 w-4 mr-2" />
                  Filter
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Status</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => setFilter('all')}>
                  All
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setFilter('published')}>
                  Published
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setFilter('draft')}>
                  Draft
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-full sm:w-auto">
                  <ArrowUpDown className="h-4 w-4 mr-2" />
                  Sort
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Sort By</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => setSortBy('newest')}>
                  Newest First
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy('oldest')}>
                  Oldest First
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy('title')}>
                  Title (A-Z)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
            </div>
          ) : error ? (
            <div className="p-8 text-center">
              <p className="text-muted-foreground">Error loading blog posts. Please try again.</p>
            </div>
          ) : (
            <div className="rounded-md border">
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] divide-y md:divide-y-0">
                {filteredPosts.length > 0 ? (
                  filteredPosts.map((post) => {
                    const readingTimeMinutes = post.content 
                      ? calculateReadingTime(post.content) 
                      : 0;
                    
                    return (
                      <div
                        key={post.id}
                        className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] items-center p-4 hover:bg-muted/50 cursor-pointer"
                        onClick={() => navigate(`/dashboard/blogs/${post.id}`)}
                      >
                        <div className="space-y-1">
                          <div className="font-medium">{post.title}</div>
                          <div className="text-sm text-muted-foreground">
                            By {post.author ? `${post.author.first_name || ''} ${post.author.last_name || ''}` : 'Unknown'} · {formatDate(post.created_at)}
                            {readingTimeMinutes > 0 && (
                              <span className="flex items-center inline-flex ml-2">
                                <Clock className="h-3 w-3 mr-1" />
                                {readingTimeMinutes} min{readingTimeMinutes !== 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {post.tags && post.tags.map((tag) => (
                              <Badge key={tag.id} variant="secondary" className="text-xs">
                                {tag.name}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <div className="text-right md:text-center mt-2 md:mt-0 md:ml-4">
                          <Badge
                            variant={post.status === 'published' ? 'default' : 'outline'}
                            className="capitalize"
                          >
                            {post.status}
                          </Badge>
                        </div>
                        <div className="flex justify-end items-center mt-4 md:mt-0 md:ml-4 gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => handleExportSingle(post, e)}
                            title="Export to PDF"
                          >
                            <FileDown className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/dashboard/blogs/${post.id}/edit`);
                            }}
                          >
                            Edit
                          </Button>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="p-8 text-center">
                    <p className="text-muted-foreground">No blog posts found. Try adjusting your filters or create a new post.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BlogsPage;
